import { z } from "zod";

import {
  DefaultProcessStepSchema,
  CompanyMachineSchema,
  CompanySettingsSchema,
  CompanySurfaceTreatmentSchema,
  IndustrialProcessSchema,
  MaterialVolumetricSchema,
  PartLevelSchema,
  ProductTypeSchema,
} from "./PrismaZod";

export const CompanyConfigVersionSchema = z.literal("1.0");

export const CompanySettingsExportSchema = CompanySettingsSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  modelId: true,
}).extend({
  surfaceTreatments: CompanySurfaceTreatmentSchema.array(),
});

export const CompanyMachineExportSchema = CompanyMachineSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  companyId: true,
  machineId: true,
}).extend({
  machineName: z.string(),
  machineBrand: z.string().nullable(),
  industrialProcess: IndustrialProcessSchema,
});

export const MaterialVolumetricExportSchema = MaterialVolumetricSchema.pick({
  materialGrade: true,
  name: true,
  volumetricPrice: true,
});

export const MaterialSupplierExportSchema = z.object({
  materialName: z.string(),
  materialType: z.string(),
  materialGrade: z.string(),
  height: z.number(),
  width: z.number(),
  length: z.number(),
  tubeThickness: z.number().nullable(),
  density: z.number().nullable(),
  price: z.number(),
});

export const DefaultProductExportSchema = z.object({
  name: z.string(),
  price: z.number(),
  fixedPrice: z.number().nullable(),
  type: ProductTypeSchema,
});

export const DefaultProcessStepExportSchema = DefaultProcessStepSchema.pick({
  industrialProcess: true,
  index: true,
  description: true,
  preparationTimeFormula: true,
  cycleTimeFormula: true,
  machineHourlyPrice: true,
}).extend({
  partLevels: PartLevelSchema.array().default(PartLevelSchema.options),
});

export const CompanyConfigExportSchema = z.object({
  version: CompanyConfigVersionSchema,
  exportedAt: z.string(),
  modelKey: z.string().nullable(),
  companySettings: CompanySettingsExportSchema,
  companyMachines: z.array(CompanyMachineExportSchema),
  materialVolumetricPrices: z.array(MaterialVolumetricExportSchema),
  materialSuppliers: z.array(MaterialSupplierExportSchema),
  defaultProducts: z.array(DefaultProductExportSchema),
  defaultProcessSteps: z.array(DefaultProcessStepExportSchema),
});

export type CompanyConfigExport = z.infer<typeof CompanyConfigExportSchema>;
export type CompanySettingsExport = z.infer<typeof CompanySettingsExportSchema>;
export type CompanyMachineExport = z.infer<typeof CompanyMachineExportSchema>;
export type MaterialVolumetricExport = z.infer<typeof MaterialVolumetricExportSchema>;
export type MaterialSupplierExport = z.infer<typeof MaterialSupplierExportSchema>;
export type DefaultProductExport = z.infer<typeof DefaultProductExportSchema>;
export type DefaultProcessStepExport = z.infer<typeof DefaultProcessStepExportSchema>;
